/**
 * Device WebSocket Service
 * Handles device-related WebSocket messages and Redux integration
 */

import { AppDispatch } from '@/store/index';
import { WebSocketMessage } from './types';
import { WebSocketMessageRouter } from './messageRouter';

export class DeviceWebSocketService {
  readonly name = 'device-service';
  readonly version = '1.0.0';
  readonly dependencies: string[] = [];
  
  private router: WebSocketMessageRouter | null = null;
  private dispatch: AppDispatch | null = null;

  /** Pending debounced weather-refetch timers, keyed by farm_id. */
  private weatherRefetchTimers = new Map<string, ReturnType<typeof setTimeout>>();
  
  // Lifecycle methods
  async initialize(router: WebSocketMessageRouter, dispatch: AppDispatch): Promise<void> {
    this.router = router;
    this.dispatch = dispatch;
    
    console.log('🔧 DeviceWebSocketService - Initializing...');
    
    // Register handlers for device-related messages
    router.registerHandler({
      messageType: 'device_update',
      handler: this.handleDeviceUpdate.bind(this),
      priority: 10,
      service: this.name
    });

    router.registerHandler({
      messageType: 'sensor_update',
      handler: this.handleSensorUpdate.bind(this),
      priority: 10,
      service: this.name
    });

    console.log('✅ DeviceWebSocketService - Initialized');
  }
  
  async cleanup(): Promise<void> {
    console.log('🧹 DeviceWebSocketService - Cleaning up...');

    if (this.router) {
      this.router.unregisterHandler('device_update', this.name);
      this.router.unregisterHandler('sensor_update', this.name);
    }
    
    this.router = null;
    this.dispatch = null;
    
    console.log('✅ DeviceWebSocketService - Cleanup complete');
  }
  
  // Message handling
  getMessageTypes(): string[] {
    return ['device_update', 'sensor_update'];
  }
  
  getHandlerPriority(): number {
    return 10;
  }
  
  // Service information
  getServiceInfo() {
    return {
      name: this.name,
      description: 'Handles device-related WebSocket messages and updates',
      version: this.version,
      author: 'WebSocket Consolidation Team',
      supportedMessageTypes: this.getMessageTypes(),
      dependencies: this.dependencies,
      priority: this.getHandlerPriority()
    };
  }
  
  // Message handlers
  private handleDeviceUpdate(message: WebSocketMessage): void {
    console.log('🔄 DeviceWebSocketService - Handling device update:', message);
    
    if (!this.dispatch) {
      console.error('❌ DeviceWebSocketService - Dispatch not available');
      return;
    }
    
    const deviceData = message.data;
    
    if (!deviceData || !deviceData.dev_eui) {
      console.warn('⚠️ DeviceWebSocketService - Invalid device update message:', deviceData);
      return;
    }
    
    try {
      // Normalize device data for consistency
      const normalizedData = this.normalizeDeviceData(deviceData);
      
      // Import and dispatch the updateDeviceStatus action
      import('@/store/slices/deviceSlice').then(({ updateDeviceStatus }) => {
        this.dispatch!(updateDeviceStatus({
          devEui: deviceData.dev_eui,
          updates: {
            last_seen_timestamp: deviceData.last_seen_timestamp,
            last_uplink_data: normalizedData.last_uplink_data
          }
        }));
        
        console.log('✅ DeviceWebSocketService - Device update dispatched to Redux');
      }).catch((error) => {
        console.error('❌ DeviceWebSocketService - Failed to import deviceSlice:', error);
      });
      
    } catch (error) {
      console.error('❌ DeviceWebSocketService - Error handling device update:', error, deviceData);
    }
  }
  
  // Handle sensor_update from non-MQTT devices (weather bridge)
  private handleSensorUpdate(message: WebSocketMessage): void {
    console.log('🌡️ DeviceWebSocketService - Handling sensor update:', message);

    if (!this.dispatch) {
      console.error('❌ DeviceWebSocketService - Dispatch not available');
      return;
    }

    const sensorData = message.data;
    if (!sensorData || !sensorData.dev_eui) {
      console.warn('⚠️ DeviceWebSocketService - Invalid sensor update message:', sensorData);
      return;
    }

    try {
      // Always refresh the device's last_seen so the device list reflects
      // the new uplink.
      import('@/store/slices/deviceSlice').then(({ updateDeviceStatus }) => {
        this.dispatch!(updateDeviceStatus({
          devEui: sensorData.dev_eui,
          updates: {
            last_seen_timestamp: sensorData.latest_timestamp
              ? new Date(sensorData.latest_timestamp).getTime() / 1000
              : Date.now() / 1000,
          }
        }));
        console.log('✅ DeviceWebSocketService - Sensor update dispatched to Redux');
      }).catch((error) => {
        console.error('❌ DeviceWebSocketService - Failed to import deviceSlice:', error);
      });

      // For weather-station bridge updates, invalidate the farm's cached
      // weather payload and refetch so the dashboard tiles, home-page
      // chips, and farm weather widget stay in sync without requiring a
      // manual reload. Backend broadcast includes farm_id and device_type
      // — only act when both are present.
      //
      // Debounced 10s per farm: a single station uplink fans out as several
      // sensor_update events (one per decoded field), and each connected
      // client receives all of them — without the debounce every uplink
      // multiplied into N refetches × M open clients against the API.
      if (sensorData.farm_id && sensorData.device_type === 'WeatherStation') {
        const farmId: string = sensorData.farm_id;
        const pending = this.weatherRefetchTimers.get(farmId);
        if (pending) clearTimeout(pending);
        this.weatherRefetchTimers.set(farmId, setTimeout(() => {
          this.weatherRefetchTimers.delete(farmId);
          import('@/store/slices/farmSlice').then(({ invalidateFarmWeather, fetchFarmWeather }) => {
            this.dispatch!(invalidateFarmWeather(farmId));
            this.dispatch!(fetchFarmWeather(farmId) as unknown as any);
            console.log(
              `🌤️ DeviceWebSocketService - Weather refetch triggered for farm ${farmId}`,
            );
          }).catch((err) => {
            console.error('❌ DeviceWebSocketService - Failed to import farmSlice:', err);
          });
        }, 10_000));
      }
    } catch (error) {
      console.error('❌ DeviceWebSocketService - Error handling sensor update:', error, sensorData);
    }
  }

  // Normalize device data for consistent processing
  private normalizeDeviceData(deviceData: any) {
    return {
      ...deviceData,
      deviceName: deviceData.alias || deviceData.deviceName || 
                  deviceData.last_uplink_data?.deviceName || 
                  `Device-${deviceData.dev_eui.slice(-4)}`,
      last_uplink_data: {
        ...deviceData.last_uplink_data,
        ...(deviceData.deviceName && { deviceName: deviceData.deviceName }),
        ...(deviceData.battery_level_percent && { battery_level_percent: deviceData.battery_level_percent }),
        ...(deviceData.valve1_status && { valve1_status: deviceData.valve1_status }),
        ...(deviceData.valve2_status && { valve2_status: deviceData.valve2_status }),
        ...(deviceData.rssi && { rssi: deviceData.rssi }),
        ...(deviceData.snr && { snr: deviceData.snr }),
      }
    };
  }
}

// Export singleton instance
export const deviceWebSocketService = new DeviceWebSocketService();